/**
 * Rotte del back-office.
 *
 * La struttura ricalca la barra laterale del wireframe §6: un gruppo di rotte
 * di redazione, accessibili a entrambi i ruoli, e un gruppo di configurazione
 * annidato sotto `RottaSuperAdmin`. Le rotte riservate esistono anche per il
 * redattore — vi si arriva digitando l'indirizzo — ma restituiscono un rifiuto
 * esplicito invece di una pagina bianca, e comunque l'API non eseguirebbe le
 * operazioni sottostanti.
 */

import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { FornitoreAutenticazione } from "./auth/FornitoreAutenticazione";
import { destinazioneDaAllerte } from "./lib/allerte";
import { RottaProtetta, RottaSuperAdmin } from "./auth/RottaProtetta";
import { Guscio } from "./layout/Guscio";
import { Accesso } from "./pagine/Accesso";
import { Alberatura } from "./pagine/Alberatura";
import { Accessibilita } from "./pagine/Accessibilita";
import { Conformita } from "./pagine/Conformita";
import { Cruscotto } from "./pagine/Cruscotto";
import { Enti } from "./pagine/Enti";
import { GestioneDocumenti } from "./pagine/GestioneDocumenti";
import { NonTrovata } from "./pagine/NonTrovata";
import { Profilo } from "./pagine/Profilo";
import { Pubblicazioni } from "./pagine/Pubblicazioni";
import { ScadenzeEAllerte } from "./pagine/ScadenzeEAllerte";
import { SchemiAnac } from "./pagine/SchemiAnac";
import { Utenti } from "./pagine/Utenti";
import { FornitoreTema } from "./tema/FornitoreTema";

export function App() {
  return (
    <FornitoreTema>
      <BrowserRouter>
        <FornitoreAutenticazione>
          <Routes>
            <Route path="/accesso" element={<Accesso />} />

            <Route element={<RottaProtetta />}>
              <Route element={<Guscio />}>
                {/* Redazione — entrambi i ruoli */}
                <Route index element={<Cruscotto />} />
                <Route path="pubblicazioni" element={<Pubblicazioni />} />
                {/* Unica via di caricamento: file direttamente sull'obbligo
                    e organizzazione in cartelle. La pubblicazione non e' qui —
                    si comanda dall'alberatura, dove il contenuto si vede al
                    suo posto nell'albero. */}
                <Route path="documenti" element={<GestioneDocumenti />} />
                {/* Scadenze e allerte in una pagina sola: che cosa e' scaduto
                    e che cosa sta per scadere. Di redazione, non di
                    configurazione: il perimetro lo verifica l'API. */}
                <Route path="scadenzario" element={<ScadenzeEAllerte />} />
                {/* Le allerte avevano una pagina loro: i collegamenti salvati
                    arrivano ancora qui, e devono atterrare sullo stesso filtro. */}
                <Route path="allerte" element={<RinvioAllerte />} />
                {/* I documenti da correggere sono sparsi fra obblighi diversi,
                    e «Documenti e cartelle» ne mostra uno per volta: chi deve
                    rimediare parte dal problema, non dall'obbligo. Di
                    redazione, non di configurazione — il perimetro lo verifica
                    l'API. */}
                <Route path="accessibilita" element={<Accessibilita />} />
                <Route path="profilo" element={<Profilo />} />

                {/* Configurazione — solo super admin */}
                <Route element={<RottaSuperAdmin />}>
                  <Route path="alberatura" element={<Alberatura />} />
                  <Route path="schemi" element={<SchemiAnac />} />
                  <Route path="utenti" element={<Utenti />} />
                  <Route path="enti" element={<Enti />} />
                  <Route path="conformita" element={<Conformita />} />
                </Route>

                <Route path="404" element={<NonTrovata />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Route>
            </Route>
          </Routes>
        </FornitoreAutenticazione>
      </BrowserRouter>
    </FornitoreTema>
  );
}

/**
 * `/allerte` → `/scadenzario`, con il filtro che l'indirizzo vecchio esprimeva
 * (la regola sta in `lib/allerte`, `destinazioneDaAllerte`).
 */
function RinvioAllerte() {
  const [parametri] = useSearchParams();
  return <Navigate to={destinazioneDaAllerte(parametri)} replace />;
}
